/**
 * SceneManager - Orchestrates the main scene with dome and model components
 * Manages composite stages with skybox (images/videos), 3D models, and audio
 */

import { Scene } from '@babylonjs/core/scene';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { SpotLight } from '@babylonjs/core/Lights/spotLight';
import { Light } from '@babylonjs/core/Lights/light';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { VideoTexture } from '@babylonjs/core/Materials/Textures/videoTexture';
import { DomeComponent } from './DomeComponent';
import { ModelComponent } from './ModelComponent';
import { PlaneComponent } from './PlaneComponent';
import { HotspotComponent } from './HotspotComponent';
import { PortalPreviewManager } from './PortalPreviewManager';
import { FloorArrowManager } from './FloorArrowManager';
import type { MediaItem } from '../media/MediaConfig';
import type { ExperienceStage, ModelConfig, PlaneConfig, LightConfig, HotspotConfig } from '../ui/UIManager';
import type { IAnalyticsService } from '../services/interfaces';
import { DeviceDetector } from '../utils/DeviceDetector';
import { Logger } from '../utils/Logger';
// Import AudioEngineV2 functions for background audio
import { CreateStreamingSoundAsync } from '@babylonjs/core/AudioV2/abstractAudio/audioEngineV2';
import { CreateAudioEngineAsync } from '@babylonjs/core/AudioV2/webAudio/webAudioEngine';
import type { AbstractSound } from '@babylonjs/core/AudioV2/abstractAudio/abstractSound';
import { SoundState } from '@babylonjs/core/AudioV2/soundState';

// Infer AudioEngineV2 type from factory function return
type AudioEngineV2 = Awaited<ReturnType<typeof CreateAudioEngineAsync>>;

// Create logger instance for this module
const log = Logger.getLogger('SceneManager');

export class SceneManager {
  private scene: Scene;
  private sceneContentParent: TransformNode; // Parent for dome, hotspots, arrows, models, planes
  private domeComponent: DomeComponent; // Manages 360° skybox (images and videos)
  private modelComponent: ModelComponent; // Manages 3D models
  private planeComponent: PlaneComponent; // Manages 2D image/video planes
  private hotspotComponent: HotspotComponent; // Manages interactive hotspots
  private portalPreviewManager: PortalPreviewManager; // Manages portal preview effects
  private floorArrowManager: FloorArrowManager; // Manages floor arrow navigation decals
  private currentAudio: AbstractSound | null = null; // AudioEngineV2 sound for scene background audio
  private globalAudio: AbstractSound | null = null; // AudioEngineV2 sound for global tour background audio
  private globalAudioUrl: string | null = null; // Track global audio URL
  private audioEngine: AudioEngineV2 | null = null;
  private loadedModelUrls: string[] = []; // Track loaded models for cleanup
  private currentLights: Light[] = []; // Track current stage lights
  public analyticsService?: IAnalyticsService; // Injected from main.ts
  private loadAbortController: AbortController | null = null; // For cancellable loads
  private allStages: ExperienceStage[] = []; // All stages in the experience (for portal previews)
  private textureCachingEnabled: boolean = true; // Enable/disable texture caching (auto-disabled on iOS)

  // State tracking for smart asset diffing (avoid reloading identical URLs)
  private currentSkyboxUrl: string | null = null;
  private currentSkyboxType: 'image' | 'video' | 'color' | null = null;
  private currentAudioUrl: string | null = null;
  private currentModelConfigs: ModelConfig[] = [];
  private currentPlaneConfigs: PlaneConfig[] = [];
  private currentHotspotConfigs: HotspotConfig[] = [];

  constructor(scene: Scene) {
    this.scene = scene;

    // Use centralized logger instead of console.log
    log.debug('Constructor called - creating components');

    // Auto-disable texture caching on iOS to avoid memory limits
    if (DeviceDetector.isIOS()) {
      this.textureCachingEnabled = false;
      log.info('iOS detected - texture caching disabled to prevent memory issues');
    } else {
      log.info('Texture caching enabled - stages will be cached for instant navigation');
    }

    // Create parent node for all scene content (enables grouped rotation for skybox offset)
    this.sceneContentParent = new TransformNode('sceneContentParent', scene);

    // Create component instances (ONLY ONCE)
    this.domeComponent = new DomeComponent(scene);
    this.modelComponent = new ModelComponent(scene);
    this.planeComponent = new PlaneComponent(scene);
    this.hotspotComponent = new HotspotComponent(scene);
    this.portalPreviewManager = new PortalPreviewManager(scene, this);
    this.floorArrowManager = new FloorArrowManager(scene);

    // Set parent node on all positionable components (dome, hotspots, portals, arrows, models, planes)
    this.domeComponent.setParentNode(this.sceneContentParent);
    this.hotspotComponent.setParentNode(this.sceneContentParent);
    this.portalPreviewManager.setParentNode(this.sceneContentParent);
    this.floorArrowManager.setParentNode(this.sceneContentParent);
    this.modelComponent.setParentNode(this.sceneContentParent);
    this.planeComponent.setParentNode(this.sceneContentParent);

    // Setup global audio unlock
    this.setupAudioUnlock();
  }

  /**
   * Set the AudioEngineV2 instance for spatial and background audio
   */
  setAudioEngine(audioEngine: AudioEngineV2): void {
    this.audioEngine = audioEngine;
    this.hotspotComponent.setAudioEngine(audioEngine);
  }

  /**
   * Initialize global background audio that plays across all scenes
   * Called once when the tour is first loaded
   */
  async initializeGlobalAudio(globalAudioConfig?: {
    url: string;
    hlsUrl?: string;
    title?: string;
    volume?: number;
    loop?: boolean;
  }): Promise<void> {
    if (!globalAudioConfig?.url) {
      log.debug('No global audio configured');
      return;
    }

    if (!this.audioEngine) {
      log.warn('AudioEngineV2 not initialized - cannot load global audio');
      return;
    }

    // Don't reload if same URL already playing
    if (this.globalAudioUrl === globalAudioConfig.url && this.globalAudio) {
      log.debug('Global audio already loaded with same URL');
      return;
    }

    // Stop any existing global audio
    if (this.globalAudio) {
      try {
        this.globalAudio.stop();
        this.globalAudio.dispose();
      } catch (err) {
        log.warn('Error disposing previous global audio:', err);
      }
      this.globalAudio = null;
    }

    try {
      const audioUrl = globalAudioConfig.hlsUrl || globalAudioConfig.url;
      log.info('Loading global background audio:', audioUrl);

      // AudioV2 CreateStreamingSoundAsync has complex overloaded signatures
      // that don't match our usage pattern - explicit cast required
      const globalAudioOptions = {
        loop: globalAudioConfig.loop !== false, // Default to true
        autoplay: false,
        maxInstances: 1,
        preloadCount: 1,
        volume: globalAudioConfig.volume ?? 1.0,
        spatialEnabled: false // Non-spatial for background audio
      };
      this.globalAudio = await CreateStreamingSoundAsync(
        'global_background_audio',
        audioUrl,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        globalAudioOptions as any,
        this.audioEngine
      );

      this.globalAudioUrl = globalAudioConfig.url;

      this.playGlobalAudioOnce();

    } catch (error) {
      log.error('Failed to load global audio:', error);
    }
  }

  /**
   * Stop global audio (only called on full cleanup, not scene transitions)
   */
  stopGlobalAudio(): void {
    if (this.globalAudio) {
      try {
        this.globalAudio.stop();
        log.debug('Global audio stopped');
      } catch (err) {
        log.warn('Error stopping global audio:', err);
      }
    }
  }

  /**
   * Get hotspot sounds for debugging
   */
  getHotspotSounds(): ReturnType<HotspotComponent['getDebugInfo']> {
    return this.hotspotComponent.getDebugInfo();
  }

  /**
   * Setup global audio unlock on first user interaction
   * FIXED: Always remove listeners after first interaction to prevent memory leaks
   */
  private setupAudioUnlock(): void {
    let hasAttempted = false;

    const removeAllListeners = () => {
      window.removeEventListener('click', unlockHandler);
      window.removeEventListener('touchstart', unlockHandler);
      window.removeEventListener('keydown', unlockHandler);
      window.removeEventListener('pointerdown', unlockHandler);
    };

    const unlockHandler = async () => {
      // Only attempt once - remove listeners immediately to prevent memory leaks
      if (hasAttempted) return;
      hasAttempted = true;

      // CRITICAL: Always remove listeners after first attempt, regardless of success
      // This prevents memory leaks when there's no audio to play
      removeAllListeners();

      log.debug('User interaction detected for audio unlock');

      // Unlock the AudioEngineV2 context first
      if (this.audioEngine) {
        try {
          // @ts-ignore
          const context = this.audioEngine.audioContext;
          if (context) {
            if (context.state === 'suspended') {
              try {
                await context.resume();
                log.debug('AudioContext resumed successfully');
              } catch (resumeError) {
                log.warn('AudioContext resume failed:', resumeError);
              }
            }

            this.playCurrentAudioOnce();
            this.playGlobalAudioOnce();
          }
        } catch (err) {
          log.warn('Error unlocking audio:', err);
        }
      }
    };

    // Add more event types for better coverage
    window.addEventListener('click', unlockHandler);
    window.addEventListener('touchstart', unlockHandler);
    window.addEventListener('keydown', unlockHandler);
    window.addEventListener('pointerdown', unlockHandler);
  }

  /**
   * AudioEngineV2 streaming sounds create a new media element on each play().
   * Guard every background play request so browser unlock handlers cannot stack
   * multiple looped instances of the same track.
   */
  private isSoundStartingOrPlaying(sound: AbstractSound): boolean {
    return sound.activeInstancesCount > 0 ||
      sound.state === SoundState.Starting ||
      sound.state === SoundState.Started ||
      sound.state === SoundState.Paused;
  }

  private playSoundOnce(sound: AbstractSound, label: string): boolean {
    if (this.isSoundStartingOrPlaying(sound)) {
      log.debug(`${label} already starting/playing; skipping duplicate play()`);
      return false;
    }

    try {
      sound.play();
      log.info(`${label} playback requested`);
      return true;
    } catch (_playErr) {
      log.debug(`${label} queued - will play after user interaction`);
      return false;
    }
  }

  /**
   * Starts the current scene background audio only if it is not already active.
   */
  playCurrentAudioOnce(): boolean {
    if (!this.currentAudio) return false;
    return this.playSoundOnce(this.currentAudio, 'Scene background audio');
  }

  /**
   * Starts global tour audio only if it is not already active.
   */
  playGlobalAudioOnce(): boolean {
    if (!this.globalAudio) return false;
    return this.playSoundOnce(this.globalAudio, 'Global background audio');
  }

  /**
   * Set all stages (needed for portal preview system)
   */
  setAllStages(stages: ExperienceStage[]): void {
    this.allStages = stages;
  }

  /**
   * Get current stage (for portal rotation compensation)
   */
  getCurrentStage(): ExperienceStage | null {
    if (!this.currentStageId || !this.allStages) return null;
    return this.allStages.find(s => s.id === this.currentStageId) || null;
  }

  /**
   * Enable or disable texture caching
   * @param enabled Whether to enable texture caching
   */
  setTextureCaching(enabled: boolean): void {
    const wasEnabled = this.textureCachingEnabled;
    this.textureCachingEnabled = enabled;

    if (wasEnabled && !enabled) {
      // Caching was disabled - clear the cache
      log.info('Texture caching disabled - clearing cache');
      this.clearCache();
    } else if (!wasEnabled && enabled) {
      log.info('Texture caching enabled');
    }
  }

  /**
   * Get current texture caching status
   */
  isTextureCachingEnabled(): boolean {
    return this.textureCachingEnabled;
  }

  /**
   * Clear all cached assets
   */
  clearCache(): void {
    log.debug(`Clearing cache (${this.assetCache.size} entries)`);
    for (const [_stageId, entry] of this.assetCache.entries()) {
      this.domeComponent.disposeOldTexture(entry.texture, entry.type);
      // Dispose AudioEngineV2 sound
      if (entry.audio) {
        try {
          entry.audio.stop();
          entry.audio.dispose();
        } catch (err) {
          log.warn('Error disposing cached audio:', err);
        }
      }
    }
    this.assetCache.clear();
  }

  /**
   * Diff model configurations between current and new stage
   * Returns which models to keep, remove, and add
   */
  private diffModelConfigs(newConfigs: ModelConfig[]): {
    toKeep: ModelConfig[];
    toRemove: ModelConfig[];
    toAdd: ModelConfig[];
  } {
    const currentUrls = new Set(this.currentModelConfigs.map(c => c.url));
    const newUrls = new Set(newConfigs.map(c => c.url));

    const toKeep = newConfigs.filter(c => currentUrls.has(c.url));
    const toRemove = this.currentModelConfigs.filter(c => !newUrls.has(c.url));
    const toAdd = newConfigs.filter(c => !currentUrls.has(c.url));

    return { toKeep, toRemove, toAdd };
  }

  /**
   * Diff plane configurations between current and new stage
   * Returns which planes to keep, remove, and add
   */
  private diffPlaneConfigs(newConfigs: PlaneConfig[]): {
    toKeep: PlaneConfig[];
    toRemove: PlaneConfig[];
    toAdd: PlaneConfig[];
  } {
    const currentUrls = new Set(this.currentPlaneConfigs.map(c => c.url));
    const newUrls = new Set(newConfigs.map(c => c.url));

    const toKeep = newConfigs.filter(c => currentUrls.has(c.url));
    const toRemove = this.currentPlaneConfigs.filter(c => !newUrls.has(c.url));
    const toAdd = newConfigs.filter(c => !currentUrls.has(c.url));

    return { toKeep, toRemove, toAdd };
  }

  /**
   * Diff hotspot configurations between current and new stage
   * Returns which hotspots to keep, remove, and add
   */
  private diffHotspotConfigs(newConfigs: HotspotConfig[]): {
    toKeep: HotspotConfig[];
    toRemove: HotspotConfig[];
    toAdd: HotspotConfig[];
  } {
    const currentMap = new Map(this.currentHotspotConfigs.map(c => [c.id, c]));
    const newMap = new Map(newConfigs.map(c => [c.id, c]));

    const toKeep: HotspotConfig[] = [];
    const toRemove: HotspotConfig[] = [];
    const toAdd: HotspotConfig[] = [];

    // Check for removals
    this.currentHotspotConfigs.forEach(current => {
      if (!newMap.has(current.id)) {
        toRemove.push(current);
      }
    });

    // Check for additions and updates
    newConfigs.forEach(newConfig => {
      const currentConfig = currentMap.get(newConfig.id);
      
      if (!currentConfig) {
        // New ID -> Add
        toAdd.push(newConfig);
      } else {
        // Existing ID -> Check if critical properties changed
        // Check both targetStageId and targetSceneId (legacy vs new field names)
        const hasChanged =
          currentConfig.targetStageId !== newConfig.targetStageId ||
          currentConfig.targetSceneId !== newConfig.targetSceneId ||
          currentConfig.type !== newConfig.type ||
          currentConfig.portalEffectStyle !== newConfig.portalEffectStyle ||
          currentConfig.position.x !== newConfig.position.x ||
          currentConfig.position.y !== newConfig.position.y ||
          currentConfig.position.z !== newConfig.position.z;

        if (hasChanged) {
          // Changed -> Remove old, Add new (Force refresh)
          toRemove.push(currentConfig);
          toAdd.push(newConfig);
        } else {
          // Unchanged -> Keep
          toKeep.push(newConfig);
        }
      }
    });

    return { toKeep, toRemove, toAdd };
  }

  /**
   * Load a composite stage (skybox + models + audio) with smooth transitions
   * @param stage The stage to load
   * @param transferredTexture Optional pre-loaded texture from portal preview to avoid reloading
   * @param onProgress Optional callback for loading progress (percent: 0-100, status: string)
   */
  // Cache for visited stages to enable instant back-navigation
  private assetCache: Map<string, {
    texture: Texture | VideoTexture,
    type: 'image' | 'video',
    audio?: AbstractSound,
    timestamp: number
  }> = new Map();
  private static readonly MAX_CACHE_SIZE = 5; // Keep only 5 stages in memory (Current + 4 others)
  private currentStageId: string | null = null;

  /**
   * Get a texture from the shared cache
   */
  public getTextureFromCache(stageId: string): Texture | VideoTexture | null {
    const entry = this.assetCache.get(stageId);
    if (entry) {
      entry.timestamp = Date.now(); // Update LRU
      return entry.texture;
    }
    return null;
  }

  /**
   * Add a texture to the shared cache
   */
  public addTextureToCache(stageId: string, texture: Texture | VideoTexture, type: 'image' | 'video'): void {
    if (this.assetCache.has(stageId)) return;

    log.debug(`Adding ${stageId} to shared cache`);
    this.assetCache.set(stageId, {
      texture,
      type,
      timestamp: Date.now()
    });

    // Prune if needed (using current stage as priority)
    if (this.currentStageId) {
      const currentStage = this.allStages.find(s => s.id === this.currentStageId);
      if (currentStage) {
        this.pruneCache(currentStage);
      }
    }
  }

  /**
   * Prune cache using Smart LRU logic
   * Priority:
   * 1. Current Stage (Keep)
   * 2. Linked Stages (Portals) (Keep)
   * 3. Linear Neighbors (Next/Prev) (Keep if space)
   * 4. Oldest (Evict)
   */
  private pruneCache(currentStage: ExperienceStage): void {
    if (this.assetCache.size <= SceneManager.MAX_CACHE_SIZE) return;

    log.debug('Pruning cache...');

    // Identify high-priority stages
    const highPriority = new Set<string>();
    
    // 1. Current Stage
    highPriority.add(currentStage.id);

    // 2. Linked Stages (Portals)
    if (currentStage.hotspots) {
      currentStage.hotspots.forEach(h => {
        if (h.type === 'navigation' && h.targetStageId) {
          highPriority.add(h.targetStageId);
        }
      });
    }

    // 3. Linear Neighbors (Next/Prev)
    const currentIndex = this.allStages.findIndex(s => s.id === currentStage.id);
    if (currentIndex !== -1) {
      if (currentIndex > 0) highPriority.add(this.allStages[currentIndex - 1].id);
      if (currentIndex < this.allStages.length - 1) highPriority.add(this.allStages[currentIndex + 1].id);
    }

    // Find candidates for eviction (not in high priority set)
    const candidates: { id: string, timestamp: number }[] = [];
    
    for (const [id, entry] of this.assetCache.entries()) {
      if (!highPriority.has(id)) {
        candidates.push({ id, timestamp: entry.timestamp });
      }
    }

    // Sort by timestamp (oldest first)
    candidates.sort((a, b) => a.timestamp - b.timestamp);

    // Evict until we are under limit
    while (this.assetCache.size > SceneManager.MAX_CACHE_SIZE && candidates.length > 0) {
      const candidate = candidates.shift();
      if (candidate) {
        this.evictStage(candidate.id);
      }
    }

    // If still over limit (e.g. too many portals), evict low-priority portals or linear neighbors?
    // For now, we just respect the limit strictly, so if we have > 5 portals, we might keep them all?
    // Actually, if highPriority > MAX_CACHE_SIZE, we can't evict anything from candidates.
    // We might need to evict from highPriority (e.g. furthest linear neighbor).
    // But let's assume 5 is enough for 1 current + 3-4 portals.
  }

  /**
   * Evict a specific stage from cache
   */
  private evictStage(stageId: string): void {
    log.debug(`Evicting ${stageId} from cache`);
    const entry = this.assetCache.get(stageId);
    if (entry) {
      this.domeComponent.disposeOldTexture(entry.texture, entry.type);
      // Dispose AudioEngineV2 sound
      if (entry.audio) {
        try {
          entry.audio.stop();
          entry.audio.dispose();
        } catch (err) {
          log.warn('Error disposing cached audio:', err);
        }
      }
      this.assetCache.delete(stageId);
    }
  }

  /**
   * Preload all stages (Skyboxes and Audio)
   * @param stages All stages to preload
   * @param onProgress Callback for global progress
   */
  async preloadAllStages(
    stages: ExperienceStage[],
    onProgress: (percent: number, status: string) => void
  ): Promise<void> {
    log.info(`Starting preload of ${stages.length} stages...`);
    
    let loadedCount = 0;
    const totalStages = stages.length;

    for (let i = 0; i < totalStages; i++) {
      const stage = stages[i];
      
      // Check if already cached
      if (this.assetCache.has(stage.id)) {
        loadedCount++;
        onProgress((loadedCount / totalStages) * 100, `${stage.name || stage.id}: Cached`);
        continue;
      }

      onProgress((loadedCount / totalStages) * 100, `${stage.name || stage.id}: Loading assets...`);

      try {
        // Load Skybox
        let texture: Texture | VideoTexture | null = null;
        let type: 'image' | 'video' = 'image';

        if (stage.skybox && stage.skybox.type !== 'color') {
          const mediaItem: MediaItem = {
            id: `${stage.id}-skybox`,
            title: stage.id,
            url: stage.skybox.url!
          };
          type = stage.skybox.type === 'video' ? 'video' : 'image';

          // Use variant-aware loading with skybox config
          texture = await this.domeComponent.loadInBackground(mediaItem, type, undefined, stage.skybox);
        }

        // Load Audio (skip for now - we'll handle AudioEngineV2 differently)
        const audio: AbstractSound | undefined = undefined;
        // Legacy audio loading removed - using AudioEngineV2 now

        // Cache it (only if caching is enabled)
        if (this.textureCachingEnabled && texture) {
          this.assetCache.set(stage.id, {
            texture: texture,
            type: type,
            audio: audio,
            timestamp: Date.now()
          });
          log.debug(`Cached preloaded stage: ${stage.id}`);
        } else if (!this.textureCachingEnabled && texture) {
          // If caching is disabled, dispose the texture after preload
          // This just verifies assets can load without keeping them in memory
          log.debug(`Preload verified for ${stage.id} - disposing (caching disabled)`);
          await this.domeComponent.disposeOldTexture(texture, type);
        }

      } catch (err) {
        log.error(`Failed to preload stage ${stage.id}`, err);
        // Continue to next stage
      }

      loadedCount++;
      onProgress((loadedCount / totalStages) * 100, `${stage.name || stage.id}: Ready`);
    }
    
    onProgress(100, 'All stages loaded');
  }

  /**
   * Load a composite stage with all assets (skybox, models, audio, planes, lights, hotspots)
   *
   * This is the main stage loading orchestration method. It handles:
   * - Cancellation of in-flight loads (prevents race conditions)
   * - Smart asset caching with LRU eviction
   * - Smooth transitions with preloaded textures from portal previews
   * - Progress reporting for loading UI
   * - Error recovery and graceful fallbacks
   *
   * ## Loading Phases
   *
   * **Phase 1: Load New Assets**
   * - Check cache for existing assets (instant load if cached)
   * - Load skybox texture (image/video/color)
   * - Load background audio (AudioEngineV2 streaming)
   * - Use transferred texture from portal preview if available
   *
   * **Phase 2: Swap to New Assets**
   * - Apply skybox texture to dome mesh
   * - Apply rotation from portal or config
   * - Start video playback if video skybox
   * - Start audio playback
   *
   * **Phase 3: Load Other Assets**
   * - Load/update 3D models with smart diffing
   * - Load/update 2D planes with smart diffing
   * - Configure lighting (custom or default)
   * - Load interactive hotspots
   * - Initialize portal preview manager for navigation hotspots
   *
   * **Phase 4: Cache/Cleanup Old Assets**
   * - Cache previous stage for instant back-navigation
   * - Prune cache using Smart LRU (prioritize portals > neighbors > oldest)
   * - Dispose uncached assets to free memory
   * - Trigger background loading of linked stages (Smart Graph Caching)
   *
   * ## Smart Diffing
   * The method compares current vs new configurations to minimize reloading:
   * - Skybox: Only reload if URL changed
   * - Models: Only reload changed models
   * - Hotspots: Only recreate changed hotspots
   *
   * ## Error Handling
   * - Individual asset failures don't crash the whole stage
   * - Aborted loads (user navigates away) are handled gracefully
   * - Progress is always monotonic (never decreases)
   *
   * @param stage - The stage configuration to load (from ExperienceConfig)
   * @param transferredTexture - Optional pre-loaded texture from portal preview.
   *   When user navigates via portal, the preview texture is transferred to avoid
   *   reloading. Includes rotation for seamless orientation matching.
   * @param onProgress - Optional callback for loading progress (0-100%, status string).
   *   Progress weights: Skybox 40%, Models 50%, Audio 10%
   * @param stageIndex - Optional current stage index (for UI display)
   * @param totalStages - Optional total stage count (for UI display)
   *
   * @returns Promise that resolves when stage is fully loaded
   *
   * @throws Never throws - errors are logged and handled internally
   *
   * @example
   * // Basic usage
   * await sceneManager.loadStage(stages[0]);
   *
   * @example
   * // With progress callback
   * await sceneManager.loadStage(stage, undefined, (percent, status) => {
   *   loadingUI.updateProgress(percent, status);
   * });
   *
   * @example
   * // With transferred texture from portal
   * const texture = portalPreviewManager.getTransferredTexture(stageId);
   * await sceneManager.loadStage(stage, texture);
   */
  async loadStage(
    stage: ExperienceStage,
    transferredTexture?: { texture: Texture | VideoTexture, type: 'image' | 'video', rotation?: number },
    onProgress?: (percent: number, status: string) => void,
    stageIndex?: number,
    totalStages?: number
  ): Promise<void> {
    const startTime = performance.now();
    log.info(`Loading composite stage: ${stage.id}`);

    // Cancel any in-flight loads
    if (this.loadAbortController) {
      log.debug('Aborting previous stage load');
      this.loadAbortController.abort();
    }

    // Create new abort controller for this load
    this.loadAbortController = new AbortController();
    const signal = this.loadAbortController.signal;

    // Reset parent rotation (will be set by skybox rotation config below)
    this.sceneContentParent.rotation.y = 0;

    // ═══════════════════════════════════════════════════════════════════════════
    // CRITICAL: Stop ALL audio FIRST before loading anything new
    // This prevents: audio doubling, background music continuing, spatial audio overlap
    // ═══════════════════════════════════════════════════════════════════════════
    this.stopAllAudio();

    // ═══════════════════════════════════════════════════════════════════════════
    // Check if we should delay hiding portal previews for video stages
    // For video stages with thumbnails, keep the center transition preview visible
    // until the video is loaded to avoid UV orientation issues
    // ═══════════════════════════════════════════════════════════════════════════
    const isVideoStageWithThumbnail = stage.skybox?.type === 'video' && stage.skybox?.thumbnailUrl;
    if (!isVideoStageWithThumbnail) {
      // For non-video stages, hide previews immediately
      this.portalPreviewManager.hideAllPreviews();
    } else {
      log.debug('Video stage with thumbnail - keeping portal preview visible during video load');
    }

    // Progress tracking helpers
    let skyboxProgress = 0;
    let modelsProgress = 0;
    let audioProgress = 0;
    let lastReportedPercent = 0;
    
    const updateTotalProgress = (status: string) => {
      if (!onProgress || signal.aborted) return;
      
      // Weights: Skybox 40%, Models 50%, Audio 10%
      let total = 0;
      let divisor = 0;
      
      if (stage.skybox && stage.skybox.type !== 'color') {
        total += skyboxProgress * 0.4;
        divisor += 0.4;
      }
      
      if (stage.models && stage.models.length > 0) {
        total += modelsProgress * 0.5;
        divisor += 0.5;
      }
      
      if (stage.audioUrl) {
        total += audioProgress * 0.1;
        divisor += 0.1;
      }
      
      let finalPercent = divisor > 0 ? (total / divisor) : 100;
      
      // Enforce monotonic progress
      if (finalPercent < lastReportedPercent) {
        finalPercent = lastReportedPercent;
      } else {
        lastReportedPercent = finalPercent;
      }
      
      // Format status string with stage count if available
      let finalStatus = status;
      if (stageIndex !== undefined && totalStages !== undefined) {
        finalStatus = `${stage.name || stage.id}: ${status}`;
      }
      
      onProgress(finalPercent, finalStatus);
    };

    try {
      if (onProgress) updateTotalProgress('Starting load...');

      // Check if aborted early
      if (signal.aborted) {
        log.debug('Stage load aborted before starting');
        return;
      }

      // Store references to old assets for cleanup/caching
      const oldAudio = this.currentAudio;
      const currentType = this.domeComponent.getCurrentType();
      const oldTexture = currentType ? {
        texture: this.domeComponent.getCurrentTexture(),
        type: currentType
      } : null;
      const previousStageId = this.currentStageId;

      // Update current stage ID
      this.currentStageId = stage.id;

      // === PHASE 1: LOAD NEW ASSETS ===

      // Check Cache First (only if caching is enabled)
      const cachedAssets = this.textureCachingEnabled ? this.assetCache.get(stage.id) : null;
      let newSkyboxTexture: Texture | VideoTexture | null = null;
      let skyboxMediaItem: MediaItem | null = null;
      let skyboxType: 'image' | 'video' | null = null;
      let skyboxNeedsSwap = false;
      let newAudio: AbstractSound | null = null;
      let audioNeedsSwap = false;

      if (cachedAssets) {
        log.debug(`Cache hit for stage ${stage.id}`);
        
        // Use cached skybox
        if (stage.skybox && stage.skybox.type !== 'color') {
          newSkyboxTexture = cachedAssets.texture;
          skyboxType = cachedAssets.type;
          skyboxMediaItem = {
            id: `${stage.id}-skybox`,
            title: stage.id,
            url: stage.skybox.url!,
          };
          skyboxNeedsSwap = true;
          skyboxProgress = 100;
          updateTotalProgress('Restoring cached skybox...');
        }

        // Use cached audio if available, otherwise load fresh
        if (cachedAssets.audio) {
          newAudio = cachedAssets.audio;
          // Stop the cached audio first - it might still be "playing" from last visit
          try {
            newAudio.stop();
            log.debug('Stopped cached audio before reuse');
          } catch (err) {
            // Ignore - might not have been playing
          }
          audioNeedsSwap = true;
          audioProgress = 100;
        } else if (stage.audioUrl && this.audioEngine) {
          // Cache doesn't have audio but stage needs it - load fresh
          log.debug('Cache hit but no audio cached - loading fresh audio');
          try {
            const cacheAudioOptions = {
              loop: true,
              autoplay: false,
              maxInstances: 1,
              preloadCount: 1,
              volume: stage.audioVolume ?? 1.0,
              spatialEnabled: false
            };
            newAudio = await CreateStreamingSoundAsync(
              `background_${stage.id}`,
              stage.audioUrl,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              cacheAudioOptions as any,
              this.audioEngine
            );

            audioNeedsSwap = true;
            audioProgress = 100;
          } catch (error) {
            log.error('Failed to load background audio:', error);
            audioProgress = 100;
          }
        }

        // Update timestamp
        cachedAssets.timestamp = Date.now();
      } else {
        // Not in cache - Load normally
        
        // 1. Load new skybox
        if (stage.skybox && stage.skybox.type !== 'color') {
          // Check if we should use transferred texture:
          // - For video skyboxes, portal previews use thumbnails (images), so we need to reload the actual video
          // - Only use transferred texture if it matches the target type
          const shouldUseTransferred = transferredTexture &&
            !(stage.skybox.type === 'video' && transferredTexture.type === 'image');

          if (shouldUseTransferred && transferredTexture) {
            log.debug('Using transferred texture');
            newSkyboxTexture = transferredTexture.texture;
            skyboxType = transferredTexture.type;
            skyboxMediaItem = { id: `${stage.id}-skybox`, title: stage.id, url: stage.skybox.url! };
            skyboxNeedsSwap = true;
            skyboxProgress = 100;
            updateTotalProgress('Skybox ready (transferred)');
          } else if (transferredTexture && stage.skybox.type === 'video' && transferredTexture.type === 'image') {
            // Video skybox with thumbnail transferred - need to load the actual video
            // IMPORTANT: Portal preview shader is kept visible during video load
            // The preview uses the thumbnail texture - DON'T dispose it yet
            log.debug('Video stage with thumbnail - portal preview showing thumbnail while video loads...');

            const newSkyboxUrl = stage.skybox.url!;
            skyboxMediaItem = { id: `${stage.id}-skybox`, title: stage.id, url: newSkyboxUrl };
            skyboxType = 'video';

            if (signal.aborted) throw new Error('Aborted');

            updateTotalProgress('Loading video skybox...');
            newSkyboxTexture = await this.domeComponent.loadInBackground(
              skyboxMediaItem,
              skyboxType,
              (percent) => {
                skyboxProgress = percent;
                updateTotalProgress(`Loading video (${Math.round(percent)}%)`);
              },
              stage.skybox
            );
            skyboxProgress = 100;
            skyboxNeedsSwap = true;

            // DON'T hide portal preview yet - wait until video is actually playing
            // This prevents black flash between portal hide and video first frame
            log.debug('Video loaded - keeping portal preview visible until video plays');
          } else {
            const newSkyboxUrl = stage.skybox.url!;
            const newSkyboxType = stage.skybox.type === 'video' ? 'video' : 'image';

            if (newSkyboxUrl === this.currentSkyboxUrl && newSkyboxType === this.currentSkyboxType) {
              log.debug('Skybox URL unchanged');
              skyboxNeedsSwap = false;
              skyboxProgress = 100;
              updateTotalProgress('Skybox unchanged');
            } else {
              log.debug('Loading new skybox...');
              skyboxMediaItem = { id: `${stage.id}-skybox`, title: stage.id, url: newSkyboxUrl };
              skyboxType = newSkyboxType;

              if (signal.aborted) throw new Error('Aborted');

              updateTotalProgress('Loading skybox...');
              newSkyboxTexture = await this.domeComponent.loadInBackground(
                skyboxMediaItem,
                skyboxType,
                (percent) => {
                  skyboxProgress = percent;
                  updateTotalProgress(`Loading skybox (${Math.round(percent)}%)`);
                },
                stage.skybox // Pass skybox config for variant selection
              );
              skyboxProgress = 100;
              skyboxNeedsSwap = true;
            }
          }
        } else if (stage.skybox && stage.skybox.type === 'color') {
          skyboxNeedsSwap = true;
          skyboxProgress = 100;
          updateTotalProgress('Skybox color ready');
        } else {
          skyboxProgress = 100;
        }

        // 2. Load new audio
        if (stage.audioUrl) {
          if (stage.audioUrl === this.currentAudioUrl && this.currentAudio) {
            log.debug('Audio URL unchanged - reusing existing sound');
            newAudio = this.currentAudio;
            audioNeedsSwap = true;
            audioProgress = 100;
            updateTotalProgress('Audio unchanged');
          } else {
            log.debug('Loading background audio with AudioEngineV2...');
            updateTotalProgress('Loading audio...');
            if (signal.aborted) throw new Error('Aborted');

            // Use AudioEngineV2 CreateStreamingSoundAsync for background audio
            const audioUrl = stage.audioUrl!;

            if (!this.audioEngine) {
              log.error('AudioEngineV2 not initialized - cannot load background audio');
              audioProgress = 100;
            } else {
              try {
                // Create streaming sound for background audio (non-spatial)
                // This is safe because we stopped ALL audio at start of loadStage()
                const stageAudioOptions = {
                  loop: true,
                  autoplay: false,
                  maxInstances: 1,
                  preloadCount: 1,
                  volume: stage.audioVolume ?? 1.0,
                  spatialEnabled: false  // Non-spatial for background audio
                };
                newAudio = await CreateStreamingSoundAsync(
                  `background_${stage.id}`,
                  audioUrl,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  stageAudioOptions as any,
                  this.audioEngine
                );

                audioNeedsSwap = true;
                audioProgress = 100;
                updateTotalProgress('Audio ready');

              } catch (error) {
                log.error('Failed to create background audio:', error);
                audioProgress = 100;
                updateTotalProgress('Audio failed');
              }
            }
          }
        } else if (!stage.audioUrl && this.currentAudio) {
          audioNeedsSwap = true;
          audioProgress = 100;
        } else {
          audioProgress = 100;
        }
      }

      // === PHASE 2: SWAP TO NEW ASSETS ===
      log.debug('Swapping to new assets...');
      updateTotalProgress('Preparing scene...');

      if (signal.aborted) {
        if (!cachedAssets) {
          newSkyboxTexture?.dispose();
          newAudio?.dispose();
        }
        throw new Error('Aborted');
      }

      // Swap skybox
      if (stage.skybox) {
        if (skyboxNeedsSwap) {
          if (stage.skybox.type === 'color') {
            const color = stage.skybox.color || '#000000';
            this.domeComponent.setSolidColor(color);
          } else if (newSkyboxTexture && skyboxMediaItem && skyboxType) {
            updateTotalProgress('Uploading texture...');
            this.domeComponent.swapTexture(newSkyboxTexture, skyboxMediaItem, skyboxType);
          }
        }

        if (stage.skybox.type !== 'color') {
          // Reset dome mesh to its local UV-flip rotation (constant)
          this.domeComponent.resetRotation();

          // Apply rotation to parent node (rotates dome + hotspots + arrows + models + planes together)
          if (transferredTexture?.rotation !== undefined) {
            // Transferred rotation is the absolute dome rotation from portal preview
            // Parent rotation = transferredRotation - Math.PI (subtract the UV flip)
            this.sceneContentParent.rotation.y = transferredTexture.rotation - Math.PI;
            log.debug(`Using transferred rotation: parent=${(this.sceneContentParent.rotation.y * 180 / Math.PI).toFixed(1)}°`);
          } else if (stage.skybox.rotation !== undefined) {
            const radians = (stage.skybox.rotation * Math.PI) / 180;
            this.sceneContentParent.rotation.y = radians;
            log.debug(`Applied stage rotation to parent: ${stage.skybox.rotation}° (${radians} rad)`);
          } else {
            this.sceneContentParent.rotation.y = 0;
          }
          // Auto-play video skyboxes (default to true if not explicitly set to false)
          if (stage.skybox.type === 'video' && stage.skybox.autoplay !== false) {
            updateTotalProgress('Starting video...');

            // Wait for video to actually start playing before hiding portal preview
            if (isVideoStageWithThumbnail) {
              // play() returns true if video started successfully (including after recovery)
              const playStarted = await this.domeComponent.play();
              if (playStarted) {
                // Small delay to ensure first frame is rendered
                await new Promise(resolve => setTimeout(resolve, 50));
                this.portalPreviewManager.hideAllPreviews();
                log.debug('Video playing - portal preview hidden');
              } else {
                // Video didn't start (autoplay blocked or error) - hide preview anyway
                this.portalPreviewManager.hideAllPreviews();
                log.debug('Video failed to autoplay - portal preview hidden');
              }
            } else {
              await this.domeComponent.play();
            }
          } else if (isVideoStageWithThumbnail) {
            // Video not autoplaying but we still need to hide portal preview
            this.portalPreviewManager.hideAllPreviews();
          }
        }
      } else {
        await this.domeComponent.unload();
      }

      // Swap audio and ENSURE it's playing
      // Old audio was already stopped at start of loadStage via stopAllAudio()
      if (audioNeedsSwap) {
        if (newAudio) {
          this.currentAudio = newAudio;
          this.playCurrentAudioOnce();
        } else {
          this.currentAudio = null;
          log.debug('No background audio for this stage');
        }
      }

      // === PHASE 3: LOAD OTHER ASSETS (Models, Planes, Lights) ===
      // Note: We don't cache models/planes yet as they are complex to detach/reattach cleanly
      // But we could in future. For now, they reload.
      
      // ... (Model/Plane/Light loading logic remains same, just ensure updateTotalProgress uses stage count)
      
      // Use smart diffing to determine what changed
      const newModelConfigs = stage.models || [];
      const newPlaneConfigs = stage.planes || [];

      const modelDiff = this.diffModelConfigs(newModelConfigs);
      const planeDiff = this.diffPlaneConfigs(newPlaneConfigs);

      // Handle models
      const modelsChanged = modelDiff.toRemove.length > 0 || modelDiff.toAdd.length > 0 ||
                           modelDiff.toKeep.length !== this.currentModelConfigs.length;

      if (modelsChanged) {
        this.modelComponent.unload();
        this.loadedModelUrls = [];

        if (newModelConfigs.length > 0) {
          let loadedCount = 0;
          const totalModels = newModelConfigs.length;
          
          for (const modelConfig of newModelConfigs) {
            if (signal.aborted) throw new Error('Aborted');
            
            updateTotalProgress(`Loading model ${loadedCount + 1}/${totalModels}...`);
            
            await this.loadModel(
              modelConfig, 
              stage.id,
              (percent) => {
                const singleModelContribution = percent / totalModels;
                const baseProgress = (loadedCount / totalModels) * 100;
                modelsProgress = baseProgress + singleModelContribution;
                updateTotalProgress(`Loading model ${loadedCount + 1}/${totalModels} (${Math.round(percent)}%)`);
              }
            );
            loadedCount++;
          }
        }
        modelsProgress = 100;
      } else if (modelDiff.toKeep.length > 0) {
        modelsProgress = 100;
        updateTotalProgress('Models unchanged');
        for (const modelConfig of newModelConfigs) {
          await this.updateModelTransform(modelConfig);
        }
      } else {
        this.modelComponent.unload();
        this.loadedModelUrls = [];
        modelsProgress = 100;
        updateTotalProgress('Scene ready');
      }

      // Handle planes
      const planesChanged = planeDiff.toRemove.length > 0 || planeDiff.toAdd.length > 0 ||
                           planeDiff.toKeep.length !== this.currentPlaneConfigs.length;

      if (planesChanged) {
        this.planeComponent.unload();
        if (newPlaneConfigs.length > 0) {
          if (signal.aborted) throw new Error('Aborted');
          await this.planeComponent.load(newPlaneConfigs);
        }
      } else {
        this.planeComponent.unload();
      }

      // Handle lights
      this.unloadLights();
      if (stage.lights && stage.lights.length > 0) {
        this.loadLights(stage.lights);
      } else {
        this.loadDefaultLighting();
      }

      // Handle hotspots
      // ... (Hotspot logic remains same)
      const newHotspotConfigs = stage.hotspots || [];
      const hotspotDiff = this.diffHotspotConfigs(newHotspotConfigs);
      const hotspotsChanged = hotspotDiff.toRemove.length > 0 || hotspotDiff.toAdd.length > 0 ||
                              hotspotDiff.toKeep.length !== this.currentHotspotConfigs.length;

      // Always dispose floor arrows when switching stages - they are scene-specific
      // and should never persist across stage transitions
      this.floorArrowManager.dispose();

      if (hotspotsChanged) {
        this.hotspotComponent.unload();
        if (newHotspotConfigs.length > 0) {
          if (signal.aborted) throw new Error('Aborted');
          await this.hotspotComponent.load(newHotspotConfigs);
          if (this.allStages.length > 0) {
            // Initialize portal previews (excludes floor-arrow style)
            await this.portalPreviewManager.initialize(newHotspotConfigs, this.allStages);
          }
        } else {
          // If new configs are empty, we just unloaded, so we are good.
          this.portalPreviewManager.dispose();
        }
      } else {
        // Hotspots unchanged - do nothing (keep existing hotspots and previews)
        log.debug('Hotspots unchanged, keeping existing');
      }

      // Always recreate floor arrows for the current stage
      if (newHotspotConfigs.length > 0 && this.allStages.length > 0) {
        const floorArrowHotspots = newHotspotConfigs.filter(
          h => h.type === 'navigation' && (h.targetSceneId || h.targetStageId) && h.portalEffectStyle === 'floor-arrow'
        );
        for (const hotspot of floorArrowHotspots) {
          // Try to find target stage position for auto-direction (check both field names)
          const targetId = hotspot.targetSceneId || hotspot.targetStageId;
          const targetStage = this.allStages.find(s => s.id === targetId);
          const targetPos = targetStage?.hotspots?.find(h => h.type === 'navigation')?.position;
          const targetVector = targetPos ? new Vector3(targetPos.x, targetPos.y, targetPos.z) : undefined;
          this.floorArrowManager.createArrow(hotspot, targetVector);
        }
      }

      // === PHASE 4: CACHE OR CLEANUP OLD ASSETS ===
      log.debug('Managing old assets...');
      updateTotalProgress('Finalizing...');

      // Cache the previous stage assets if valid (only if caching is enabled)
      if (this.textureCachingEnabled && previousStageId && oldTexture && oldTexture.texture) {
        log.debug(`Caching assets for stage ${previousStageId}`);
        this.assetCache.set(previousStageId, {
          texture: oldTexture.texture,
          type: oldTexture.type,
          audio: oldAudio || undefined,
          timestamp: Date.now()
        });

        // Prune cache using Smart LRU
        this.pruneCache(stage);
      } else {
        // If we couldn't cache (e.g. no previous stage ID), dispose
        if (oldAudio && oldAudio !== newAudio) { // Don't dispose if reused
           // Check if it's in cache before disposing? 
           // Actually, if we are here, it means we didn't cache it.
           // But wait, if we reused it (audioNeedsSwap=false), we shouldn't dispose it.
           // Logic above: if audioNeedsSwap=true, we stopped old audio.
           // If we reused it, oldAudio === newAudio (conceptually), but actually this.currentAudio was updated.
           // If audioNeedsSwap was false, oldAudio === this.currentAudio, so we shouldn't dispose.
           
           // Refined logic:
           // We only cache if we navigated AWAY from a stage.
           // If we just reloaded the same stage, previousStageId might be same as current?
           // No, we set currentStageId = stage.id earlier.
           
           // If we are not caching, we must dispose.
           // BUT, we must ensure we don't dispose what we are currently using.

           const isReusedAudio = oldAudio === this.currentAudio;
           if (!isReusedAudio && oldAudio) {
             try {
               oldAudio.stop();
               oldAudio.dispose();
               log.debug('Old background audio disposed');
             } catch (err) {
               log.warn('Error disposing old audio:', err);
             }
           }

           const isReusedTexture = oldTexture && oldTexture.texture === this.domeComponent.getCurrentTexture();

           if (!isReusedTexture && oldTexture && oldTexture.type) {
             await this.domeComponent.disposeOldTexture(oldTexture.texture, oldTexture.type);
             log.debug('Old skybox disposed');
           }
        }
      }

      // Update state tracking
      if (stage.skybox) {
        if (stage.skybox.type === 'color') {
          this.currentSkyboxUrl = null;
          this.currentSkyboxType = 'color';
        } else {
          this.currentSkyboxUrl = stage.skybox.url || null;
          this.currentSkyboxType = stage.skybox.type === 'video' ? 'video' : 'image';
        }
      } else {
        this.currentSkyboxUrl = null;
        this.currentSkyboxType = null;
      }

      this.currentAudioUrl = stage.audioUrl || null;
      this.currentModelConfigs = newModelConfigs;
      this.currentPlaneConfigs = newPlaneConfigs;
      this.currentHotspotConfigs = newHotspotConfigs;

      log.info(`Composite stage loaded: ${stage.id}`);
      if (onProgress) onProgress(100, 'Ready');

      // Track stage load time
      const loadTime = performance.now() - startTime;
      this.analyticsService?.trackStageLoadTime(
        this.analyticsService?.getCurrentStageIndex() || 0,
        loadTime
      );

      this.loadAbortController = null;

      // Trigger background loading of linked stages (Smart Graph Caching)
      this.preloadLinkedStages(stage);

    } catch (error) {
      if (signal.aborted || (error instanceof Error && error.message === 'Aborted')) {
        log.debug('Stage load was aborted');
        return;
      }

      log.error('Error loading stage:', error);
      this.loadAbortController = null;
    }
  }

  /**
   * Background load linked stages (Smart Graph Caching)
   */
  private async preloadLinkedStages(currentStage: ExperienceStage): Promise<void> {
    // 1. Identify linked stages (Portals)
    const linkedStageIds = new Set<string>();
    if (currentStage.hotspots) {
      currentStage.hotspots.forEach(h => {
        if (h.type === 'navigation' && h.targetStageId) {
          linkedStageIds.add(h.targetStageId);
        }
      });
    }

    // 2. Identify linear neighbors (Next)
    const currentIndex = this.allStages.findIndex(s => s.id === currentStage.id);
    if (currentIndex !== -1 && currentIndex < this.allStages.length - 1) {
      linkedStageIds.add(this.allStages[currentIndex + 1].id);
    }

    log.debug(`Smart Graph Caching: Preloading ${linkedStageIds.size} linked stages`, Array.from(linkedStageIds));

    // 3. Load them (if not cached)
    for (const stageId of linkedStageIds) {
      if (this.assetCache.has(stageId)) continue;

      const stage = this.allStages.find(s => s.id === stageId);
      if (!stage || !stage.skybox || stage.skybox.type === 'color') continue;

      // Skip video preloading for mobile safety (unless explicitly requested? For now, skip)
      if (stage.skybox.type === 'video') {
        log.debug(`Skipping video preload for ${stageId} (Mobile Safety)`);
        continue;
      }

      log.debug(`Background loading ${stageId}...`);
      try {
        const mediaItem: MediaItem = {
          id: `${stage.id}-skybox`,
          title: stage.id,
          url: stage.skybox.url!
        };

        // Use loadInBackground with variant support
        const texture = await this.domeComponent.loadInBackground(mediaItem, 'image', undefined, stage.skybox);
        
        // Add to cache
        this.addTextureToCache(stage.id, texture, 'image');
        
        // Also load audio? Maybe skip for now to save bandwidth/memory
      } catch (err) {
        log.warn(`Failed to background load ${stageId}`, err);
      }
    }
  }

  /**
   * Update model transform without reloading
   */
  private async updateModelTransform(modelConfig: ModelConfig): Promise<void> {
    const rootMesh = this.modelComponent.getModelRoot(modelConfig.url);
    if (!rootMesh) return;

    // Update position
    if (modelConfig.position) {
      rootMesh.position.set(
        modelConfig.position.x || 0,
        modelConfig.position.y || 0,
        modelConfig.position.z || 0
      );
    }

    // Update rotation
    if (modelConfig.rotation) {
      rootMesh.rotation.set(
        modelConfig.rotation.x || 0,
        modelConfig.rotation.y || 0,
        modelConfig.rotation.z || 0
      );
    }

    // Update scale
    if (modelConfig.scale !== undefined) {
      if (typeof modelConfig.scale === 'number') {
        rootMesh.scaling.setAll(modelConfig.scale);
      } else {
        rootMesh.scaling.set(
          modelConfig.scale.x || 1,
          modelConfig.scale.y || 1,
          modelConfig.scale.z || 1
        );
      }
    }
  }

  /**
   * Load a single model into the scene
   */
  private async loadModel(
    modelConfig: ModelConfig, 
    stageId: string, 
    onProgress?: (percent: number) => void
  ): Promise<void> {
    const modelMediaItem: MediaItem = {
      id: `${stageId}-model-${this.loadedModelUrls.length}`,
      title: `Model ${this.loadedModelUrls.length}`,
      url: modelConfig.url,
    };

    await this.modelComponent.load(modelMediaItem, onProgress);

    // Apply transformations from config
    const rootMesh = this.modelComponent.getModelRoot(modelConfig.url);
    if (rootMesh) {
      // Position
      if (modelConfig.position) {
        rootMesh.position.set(
          modelConfig.position.x || 0,
          modelConfig.position.y || 0,
          modelConfig.position.z || 0
        );
      }

      // Rotation
      if (modelConfig.rotation) {
        rootMesh.rotation.set(
          modelConfig.rotation.x || 0,
          modelConfig.rotation.y || 0,
          modelConfig.rotation.z || 0
        );
      }

      // Scale
      if (modelConfig.scale !== undefined) {
        if (typeof modelConfig.scale === 'number') {
          rootMesh.scaling.setAll(modelConfig.scale);
        } else {
          rootMesh.scaling.set(
            modelConfig.scale.x || 1,
            modelConfig.scale.y || 1,
            modelConfig.scale.z || 1
          );
        }
      }
    }

    this.loadedModelUrls.push(modelConfig.url);
    log.debug(`Model loaded: ${modelConfig.url}`);
  }

  /**
   * Get dome component (for skybox access)
   */
  getDomeComponent(): DomeComponent {
    return this.domeComponent;
  }

  /**
   * Get model component
   */
  getModelComponent(): ModelComponent {
    return this.modelComponent;
  }

  /**
   * Get plane component
   */
  getPlaneComponent(): PlaneComponent {
    return this.planeComponent;
  }

  getHotspotComponent(): HotspotComponent {
    return this.hotspotComponent;
  }

  /**
   * Get portal preview manager
   */
  getPortalPreviewManager(): PortalPreviewManager {
    return this.portalPreviewManager;
  }

  /**
   * Get floor arrow manager
   */
  getFloorArrowManager(): FloorArrowManager {
    return this.floorArrowManager;
  }

  /**
   * Get the current skybox rotation in radians (the sceneContentParent Y rotation)
   */
  getSkyboxRotation(): number {
    return this.sceneContentParent.rotation.y;
  }

  /**
   * Get the scene content parent node (for editor use)
   */
  getSceneContentParent(): TransformNode {
    return this.sceneContentParent;
  }

  /**
   * Unload current stage
   */
  unloadCurrentStage(): void {
    // CRITICAL: Stop ALL audio FIRST before unloading anything
    this.stopAllAudio();

    this.domeComponent.unload();
    this.modelComponent.unload();
    this.planeComponent.unload();
    this.hotspotComponent.unload();
    this.portalPreviewManager.dispose();
    this.floorArrowManager.dispose();

    // Dispose background audio (separate from stop)
    if (this.currentAudio) {
      try {
        this.currentAudio.dispose();
        log.debug('Background audio disposed');
      } catch (err) {
        log.warn('Error disposing background audio:', err);
      }
      this.currentAudio = null;
    }

    this.loadedModelUrls = [];

    // Clear state tracking
    this.currentSkyboxUrl = null;
    this.currentSkyboxType = null;
    this.currentAudioUrl = null;
    this.currentModelConfigs = [];
    this.currentPlaneConfigs = [];
    this.currentHotspotConfigs = [];
  }

  /**
   * Load lights from configuration
   * Lights can't be parented to TransformNode, so we manually rotate
   * their positions and directions by the skybox rotation angle.
   */
  private loadLights(lightConfigs: LightConfig[]): void {
    log.debug(`Loading ${lightConfigs.length} light(s)...`);

    const rotationY = this.sceneContentParent.rotation.y;

    lightConfigs.forEach((config, index) => {
      const light = this.createLight(config, index, rotationY);
      if (light) {
        this.currentLights.push(light);
      }
    });

    log.debug(`${this.currentLights.length} light(s) loaded`);
  }

  /**
   * Load default lighting (hemispheric light)
   */
  private loadDefaultLighting(): void {
    const defaultLight = new HemisphericLight('defaultLight', new Vector3(0, 1, 0), this.scene);
    defaultLight.intensity = 1.2;
    defaultLight.diffuse = new Color3(1, 1, 1);
    defaultLight.specular = new Color3(0.5, 0.5, 0.5);
    defaultLight.groundColor = new Color3(0.3, 0.3, 0.3);

    this.currentLights.push(defaultLight);
    log.debug('Default hemispheric light loaded');
  }

  /**
   * Rotate a Vector3 around the Y axis by the given angle
   */
  private rotateVectorY(v: Vector3, angle: number): Vector3 {
    if (angle === 0) return v;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return new Vector3(
      v.x * cos - v.z * sin,
      v.y,
      v.x * sin + v.z * cos
    );
  }

  /**
   * Create a single light from configuration
   * @param rotationY - Y rotation to apply to position/direction (for skybox rotation alignment)
   */
  private createLight(config: LightConfig, index: number, rotationY: number = 0): Light | null {
    let light: Light | null = null;
    const name = `light_${index}`;

    try {
      switch (config.type) {
        case 'hemispheric':
          {
            let direction = config.direction
              ? new Vector3(config.direction.x, config.direction.y, config.direction.z)
              : new Vector3(0, 1, 0);
            direction = this.rotateVectorY(direction, rotationY);

            const hemiLight = new HemisphericLight(name, direction, this.scene);
            hemiLight.intensity = config.intensity ?? 1.0;

            if (config.diffuse) {
              hemiLight.diffuse = Color3.FromHexString(config.diffuse);
            }
            if (config.specular) {
              hemiLight.specular = Color3.FromHexString(config.specular);
            }
            if (config.groundColor) {
              hemiLight.groundColor = Color3.FromHexString(config.groundColor);
            }

            light = hemiLight;
          }
          break;

        case 'directional':
          {
            let direction = config.direction
              ? new Vector3(config.direction.x, config.direction.y, config.direction.z)
              : new Vector3(-1, -2, -1);
            direction = this.rotateVectorY(direction, rotationY);

            const dirLight = new DirectionalLight(name, direction, this.scene);
            dirLight.intensity = config.intensity ?? 1.0;

            if (config.diffuse) {
              dirLight.diffuse = Color3.FromHexString(config.diffuse);
            }
            if (config.specular) {
              dirLight.specular = Color3.FromHexString(config.specular);
            }

            light = dirLight;
          }
          break;

        case 'point':
          {
            let position = config.position
              ? new Vector3(config.position.x, config.position.y, config.position.z)
              : new Vector3(0, 3, 0);
            position = this.rotateVectorY(position, rotationY);

            const pointLight = new PointLight(name, position, this.scene);
            pointLight.intensity = config.intensity ?? 1.5;

            if (config.diffuse) {
              pointLight.diffuse = Color3.FromHexString(config.diffuse);
            }
            if (config.specular) {
              pointLight.specular = Color3.FromHexString(config.specular);
            }
            if (config.range) {
              pointLight.range = config.range;
            }

            light = pointLight;
          }
          break;

        case 'spot':
          {
            let position = config.position
              ? new Vector3(config.position.x, config.position.y, config.position.z)
              : new Vector3(0, 5, 0);
            position = this.rotateVectorY(position, rotationY);

            let direction = config.direction
              ? new Vector3(config.direction.x, config.direction.y, config.direction.z)
              : new Vector3(0, -1, 0);
            direction = this.rotateVectorY(direction, rotationY);

            const spotLight = new SpotLight(
              name,
              position,
              direction,
              config.angle ?? 0.8,
              config.exponent ?? 2,
              this.scene
            );

            spotLight.intensity = config.intensity ?? 2.0;

            if (config.diffuse) {
              spotLight.diffuse = Color3.FromHexString(config.diffuse);
            }
            if (config.specular) {
              spotLight.specular = Color3.FromHexString(config.specular);
            }

            light = spotLight;
          }
          break;

        default:
          log.error(`Unknown light type: ${config.type}`);
      }

      if (light) {
        log.debug(`Created ${config.type} light: ${name}`);
      }

      return light;
    } catch (error) {
      log.error(`Failed to create light ${name}:`, error);
      return null;
    }
  }

  /**
   * Unload current lights
   */
  private unloadLights(): void {
    this.currentLights.forEach((light) => {
      light.dispose();
    });
    this.currentLights = [];
  }

  /**
   * Get current background audio
   */
  getCurrentAudio(): AbstractSound | null {
    return this.currentAudio;
  }

  /**
   * Get global background audio
   */
  getGlobalAudio(): AbstractSound | null {
    return this.globalAudio;
  }

  /**
   * Get current background audio info
   */
  getCurrentAudioInfo(): { url: string | null, isPlaying: boolean } {
    return {
      url: this.currentAudioUrl,
      isPlaying: this.currentAudio ? true : false
    };
  }

  /**
   * CRITICAL: Stop scene-specific audio sources (scene background + hotspot spatial audio + VIDEO AUDIO)
   * This MUST be called at the START of scene transitions to prevent ANY audio overlap
   * Does NOT dispose - just stops playback immediately
   * NOTE: Global audio is NOT stopped here - it persists across scene transitions
   */
  stopAllAudio(): void {
    log.info('STOPPING SCENE-SPECIFIC AUDIO SOURCES (global audio persists)');

    // 1. Stop scene background audio immediately (NOT global audio)
    if (this.currentAudio) {
      try {
        this.currentAudio.stop();
        log.debug('Scene background audio stopped');
      } catch (err) {
        log.warn('Error stopping scene background audio:', err);
      }
    }

    // 2. Stop ALL hotspot spatial audio (force stop regardless of state)
    try {
      this.hotspotComponent.stopAllAudio();
    } catch (err) {
      log.warn('Error stopping hotspot audio:', err);
    }

    // 3. CRITICAL: Stop video skybox audio immediately!
    // This prevents video audio from continuing to play during scene transitions
    try {
      this.domeComponent.stopVideoAudio();
    } catch (err) {
      log.warn('Error stopping video audio:', err);
    }

    // 4. Also stop any cached audio that might be playing (defensive)
    this.assetCache.forEach((entry, stageId) => {
      if (entry.audio) {
        try {
          entry.audio.stop();
          log.debug(`Stopped cached audio for ${stageId}`);
        } catch (err) {
          // Cached audio might not be playing, ignore errors
        }
      }
    });

    log.info('All audio sources stopped');
  }

  /**
   * Dispose all components and resources
   */
  dispose(): void {
    // CRITICAL: Stop ALL audio first
    this.stopAllAudio();

    // Clear the cache (stops and disposes all cached audio)
    this.clearCache();

    this.domeComponent.dispose();
    this.modelComponent.dispose();
    this.planeComponent.dispose();
    this.hotspotComponent.dispose();

    // Dispose background audio
    if (this.currentAudio) {
      try {
        this.currentAudio.dispose();
      } catch (err) {
        log.warn('Error disposing background audio:', err);
      }
      this.currentAudio = null;
    }

    this.unloadLights();
    this.loadedModelUrls = [];
  }
}
